import * as React from "react";
import { 
    List, Show,
    Datagrid, Filter, TopToolbar, SortButton, ExportButton, SimpleShowLayout,
    TextField, DateField, FunctionField, ArrayField,
    ReferenceInput, SelectInput,
    useListSortContext, useShowController
} from 'react-admin';
import { TableHead, TableRow, TableCell, useMediaQuery, makeStyles } from '@material-ui/core';
import Config from "../../providers/configProvider";
import LogoField from '../../components/LogoField';
import TimeWithZoneField from '../../components/TimeWithZoneField';

const CompetitionSeasonAndTeamFilter = props => {
    const isSmall = useMediaQuery(theme => theme.breakpoints.down('sm'));
    return (
        <Filter {...props} style={{marginTop: isSmall ? '0px' : '-16px'}}>
            <ReferenceInput label="Comp Season" source="competitionSeasonId" reference="competition-season" filter={{ competitionCode: 'NRL' }} perPage={1000} alwaysOn>
                <SelectInput optionText={record => `${record.name} ${record.seasonYear}`} />
            </ReferenceInput>
            <ReferenceInput label="Team" source="teamId" reference="team" perPage={1000} alwaysOn>
                <SelectInput optionText={<LogoField type="teams" source="code" text="name" />} disabled={!props.enableTeam} />
            </ReferenceInput>
        </Filter>
    );
};

const ListActions = ({ sortFields }) => (
    <TopToolbar>
        <SortButton fields={sortFields} />
        <ExportButton />
    </TopToolbar>
);

const useStyles = makeStyles({
    cellSmall: {
        padding: '8px'
    },
    cell: {
        textAlign: 'right'
    },
    cellLeft: {
        textAlign: 'left'
    },
    cellBorder: {
        borderLeft: '1px solid #000',
        textAlign: 'right'
    },
    cellBorderLeft: {
        borderLeft: '1px solid #000',
        textAlign: 'left'
    },
    cellBorderDouble: {
        borderLeft: '3px double #000',
        textAlign: 'right'
    },
    topHeader: {
        fontWeight: 'bold',
        textAlign: 'center'
    },
    topHeaderBorder: {
        fontWeight: 'bold',
        textAlign: 'center',
        borderLeft: '1px solid #000'
    },
    topHeaderBorderDouble: {
        fontWeight: 'bold',
        textAlign: 'center',
        borderLeft: '3px double #000'
    },
    green: {
        fontWeight: 'bold',
        color: 'green'
    },
    greenSmall: {
        color: 'green'
    },
    red: {
        fontWeight: 'bold',
        color: 'red'
    },
    redSmall: {
        color: 'red'
    }
});

/*** PLAYER STATS ***/
const playerStatsSortFields = [
    { field: 'lastName', label: 'name' }, 
    { field: 'primaryPosition.name', label: 'primary position' },  
    { field: 'teamRecord.played', label: 'played' }, 
    { field: 'teamRecord.won', label: 'won' },
    { field: 'teamRecord.drawn', label: 'drawn' },
    { field: 'teamRecord.lost', label: 'lost' },
    { field: 'teamRecord.winPercentage', label: 'win percentage' },
    { field: 'tries', label: 'tries' },
    { field: 'goals', label: 'goals' },
    { field: 'fieldGoals1', label: '1 point field goals' },
    { field: 'fieldGoals2', label: '2 point field goals' },
    { field: 'points', label: 'points' },
    { field: 'sinBins', label: 'sin bins' },
    { field: 'sendOffs', label: 'send offs' }
];

const PlayerStatHeader = ({ children }) => {
    const classes = useStyles();
    const { currentSort } = useListSortContext();
    return (
        <TableHead>
            <TableRow>
                <TableCell colSpan={2} />
                <TableCell colSpan={8} className={classes.topHeader}>Results</TableCell>
                <TableCell colSpan={7} className={classes.topHeaderBorder}>Stats</TableCell>
            </TableRow>
            <TableRow>
                {React.Children.map(children, child => (
                    <TableCell key={child.props.source} className={child.props.source && child.props.source === 'tries' ? classes.cellBorder : (child.props.label === 'Player' || child.props.label === 'Primary Position' ? classes.cellLeft : classes.cell)}>
                        {child.props.label}{playerStatsSortFields.find(f => f.field === child.props.source) && currentSort.field === playerStatsSortFields.find(f => f.field === child.props.source).label
                            ? (currentSort.order === 'DESC' ? <span> &darr;</span> : <span> &uarr;</span>)
                            :  null}
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
};

export const PlayerStatList = props => {
    const isSmall = useMediaQuery(theme => theme.breakpoints.down('sm'));
    const classes = useStyles();
    return (
        <List {...props} 
            bulkActionButtons={false}
            filters={<CompetitionSeasonAndTeamFilter enableTeam={false} />}
            filterDefaultValues={{ competitionSeasonId: Config.CurrentCompetitionSeasonId, teamId: Config.MyTeamId }}
            actions={<ListActions sortFields={playerStatsSortFields.map(f => f.label)} />}
            sort={{ field: 'name', order: 'ASC' }}
            pagination={null}
        >
            {isSmall ? (
                <Datagrid
                    classes={{ rowCell: classes.cellSmall, headerCell: classes.cellSmall }}
                    rowClick={(id, basePath, record) => `/stat/players/${record.competitionSeasonId};${record.id}/show`}
                >
                    <FunctionField render={record => 
                        <span style={{whiteSpace:'nowrap'}}><LogoField type="players" source="id" extension="png" fallback="player.png" width={30} additionalText={`${record.firstName.substring(0,1)}. ${(record.lastName.length > 8 ? `${record.lastName.substring(0,8)}…` : record.lastName)} (${record.primaryPosition.code})`} /></span>
                    } label="Player" />
                    <TextField source="teamRecord.played" label="P" />
                    <TextField source="teamRecord.winPercentage" label="W%" />   
                    <TextField source="tries" label="T" emptyText="-" />
                    <TextField source="goals" label="G" emptyText="-" />
                    <FunctionField render={record => record.fieldGoals1 !== null ? record.fieldGoals1 + record.fieldGoals2 : '-'} label="FG" />
                    <TextField source="points" label="Pts" emptyText="-" />
                </Datagrid>
            ) : (
                <Datagrid 
                    header={<PlayerStatHeader />}
                    classes={{ rowCell: classes.cell, headerCell: classes.cell }}
                    rowClick={(id, basePath, record) => `/stat/players/${record.competitionSeasonId};${record.id}/show`}
                >
                    <FunctionField source="lastName" render={record =>
                        <LogoField type="players" source="id" text="firstName" extension="png" fallback="player.png" height={48} width={58} additionalText={record.lastName} />
                    } label="Player" cellClassName={classes.cellLeft} />
                    <TextField source="primaryPosition.name" label="Primary Position" cellClassName={classes.cellLeft} />
                    <TextField source="teamRecord.played" label="P"/>
                    <TextField source="teamRecord.won" label="W" />
                    <TextField source="teamRecord.drawn" label="D" />
                    <TextField source="teamRecord.lost" label="L" />
                    <TextField source="teamRecord.winPercentage" label="W%" />
                    <TextField source="teamRecord.pointsFor" label="F" />
                    <TextField source="teamRecord.pointsAgainst" label="A" />
                    <TextField source="teamRecord.pointsDiff" label="+/-" />       
                    <TextField source="tries" label="T" emptyText="-" cellClassName={classes.cellBorder}  />
                    <TextField source="goals" label="G" emptyText="-" />
                    <TextField source="fieldGoals1" label="FG1" emptyText="-" />
                    <TextField source="fieldGoals2" label="FG2" emptyText="-" />
                    <TextField source="points" label="Pts" emptyText="-"/>
                    <TextField source="sinBins" label="SB" emptyText="-" />
                    <TextField source="sendOffs" label="SO" emptyText="-" />
                </Datagrid>
            )}
        </List>
    );
};

const PlayerMatchHeader = ({ children }) => {
    const classes = useStyles();
    return (
        <TableHead>
            <TableRow>
                <TableCell colSpan={5} className={classes.topHeader}>Match</TableCell>
                <TableCell colSpan={2} className={classes.topHeaderBorder}>Position</TableCell>
                <TableCell colSpan={7} className={classes.topHeaderBorder}>Stats</TableCell>
            </TableRow>
            <TableRow>
                {React.Children.map(children, child => (
                    <TableCell key={child.props.source} className={child.props.source && child.props.source === 'tries' ? classes.cellBorder : (child.props.source === 'position.name' ? classes.cellBorderLeft : (['Round','Opponent','Venue','Date','Time','Position','Location'].includes(child.props.label) ? classes.cellLeft : classes.cell))}>
                        {child.props.label}
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
};

export const PlayerStatShow = props => {
    const isSmall = useMediaQuery(theme => theme.breakpoints.down('sm'));
    const classes = useStyles();
    const { loading, record } = useShowController(props);
    if (loading) return null;

    const Title = ({ record }) => <span>Player Stats Breakdown - {record.data.competitionCode} {record.data.seasonYear} - {record.data.firstName} {record.data.lastName}</span>;

    return (
        <Show {...props} title={<Title />}>
            <SimpleShowLayout>
                <LogoField type="competitions" source="data.competitionCode" text="data.competitionCode" label="Competition" />
                <TextField source="data.seasonYear" label="Season" />
                <FunctionField render={rec =>
                    <LogoField type="players" source="data.playerId" text="data.firstName" extension="png" fallback="player.png" height={48} width={58} additionalText={rec.data.lastName} />
                } label="Player" cellClassName={classes.cellLeft} />
                <ArrayField source="data.matches" addLabel={false}>
                    {isSmall ? (
                        <Datagrid classes={{ rowCell: classes.cellSmall, headerCell: classes.cellSmall }}>
                            <TextField source="roundNumber" label="Rd" />
                            <LogoField type="teams" source="opponentTeamCode" text="opponentTeamCode" label="Opponent" cellClassName={classes.cellLeft} />
                            <TextField source="position.code" label="Pos" />               
                            <TextField source="tries" label="T" emptyText="-" />
                            <TextField source="goals" label="G" emptyText="-" />
                            <TextField source="fieldGoals1" label="FG1" emptyText="-" />
                            <TextField source="fieldGoals2" label="FG2" emptyText="-" />
                            <TextField source="points" label="Pts" emptyText="-" />
                        </Datagrid>
                    ) : (
                        <Datagrid
                            header={<PlayerMatchHeader />}
                            classes={{ rowCell: classes.cell, headerCell: classes.cell }}
                        > 
                            <FunctionField render={data => `${data.roundName}${data.pool ? ` (Pool ${data.pool})` : ''}`} label="Round" cellClassName={classes.cellLeft} />
                            <FunctionField render={data => record.competitionCode === 'NRL' 
                                ? <LogoField type="teams" source="opponentTeamCode" text="opponentTeamNickname" /> 
                                : <LogoField type="teams" source="opponentTeamCode" text="opponentTeamName" />
                            } label="Opponent" cellClassName={classes.cellLeft} />
                            <TextField source="venueName" label="Venue" cellClassName={classes.cellLeft} />
                            <DateField source="startDateTime" label="Date" options={{ weekday: 'short', day: 'numeric', month: 'long' }} locales="en-AU" cellClassName={classes.cellLeft} />
                            <TimeWithZoneField source="startDateTime" includeDate={false} label="Time" cellClassName={classes.cellLeft} />
                            <TextField source="position.name" label="Position" cellClassName={classes.cellBorderLeft} />
                            <TextField source="position.location" label="Location" cellClassName={classes.cellLeft} />                
                            <TextField source="tries" label="T" emptyText="-" cellClassName={classes.cellBorder} />
                            <TextField source="goals" label="G" emptyText="-" />
                            <TextField source="fieldGoals1" label="FG1" emptyText="-" />
                            <TextField source="fieldGoals2" label="FG2" emptyText="-" />
                            <TextField source="points" label="Pts" emptyText="-" />
                            <TextField source="sinBins" label="SB" emptyText="-" />
                            <TextField source="sendOffs" label="SO" emptyText="-" />
                        </Datagrid>
                    )}
                </ArrayField>
            </SimpleShowLayout>
        </Show>
    );
};
